import type { PlanningPromoDTO } from "@easyshop/shared";
import { Pill, cx } from "@/components/ui";
import { IosGroup, IosSection } from "@/components/mobile/MobileUi";
import { dateShort } from "./utils";

const STATUS = {
  ACTIVE: ["bg-positive-bg text-positive", "En cours"],
  PLANNED: ["bg-accent-tint text-accent", "À venir"],
  DONE: ["bg-page text-muted", "Terminée"],
} as const;

/** « −15 % » si une remise indicative est renseignée (anciennes campagnes). */
const offer = (p: PlanningPromoDTO) => (p.discountPct != null ? ` −${p.discountPct} %` : "");

/** Promos actives un jour donné (bandeau au-dessus des créneaux). */
export function promosOn(promos: PlanningPromoDTO[], day: string, boutiqueId?: string) {
  return promos.filter(
    (p) => p.startDate <= day && p.endDate >= day && (!boutiqueId || p.boutiqueIds.includes(boutiqueId))
  );
}

export function DayPromoChips({ promos, compact = false }: { promos: PlanningPromoDTO[]; compact?: boolean }) {
  if (promos.length === 0) return null;
  return (
    <div className="mt-1 flex flex-col gap-[3px]">
      {promos.map((p) => (
        <div
          key={p.id}
          title={[p.name + offer(p), p.description, p.productNames.join(", ")].filter(Boolean).join("\n")}
          className={cx(
            "truncate rounded-[5px] bg-positive-bg px-[5px] py-[2px] font-bold text-positive",
            compact ? "text-[10px]" : "text-[9.5px]"
          )}
        >
          {p.name}
          {offer(p)}
        </div>
      ))}
    </div>
  );
}

/** « Prochaines promos » du shell mobile (section et groupe iOS). */
export function UpcomingPromosGroup({
  promos,
  title = "Prochaines promos",
  emptyText = "Aucune promo prévue dans les 30 prochains jours.",
}: {
  promos: PlanningPromoDTO[];
  title?: string;
  emptyText?: string;
}) {
  return (
    <IosSection title={title}>
      <IosGroup>
        {promos.length === 0 && <div className="px-4 py-3 text-[15px] text-muted">{emptyText}</div>}
        {promos.map((p) => {
          const [cls, label] = STATUS[p.status];
          return (
            <div key={p.id} className="ios-sep px-4 py-[11px]">
              <div className="flex flex-wrap items-center gap-[6px]">
                <span className="text-[16px] font-medium">{p.name}</span>
                {p.discountPct != null && <span className="rounded-[6px] bg-accent px-[7px] py-[2px] text-[12px] font-semibold text-white">−{p.discountPct} %</span>}
                <span className={cx("rounded-[6px] px-[7px] py-[2px] text-[12px] font-semibold", cls)}>{label}</span>
              </div>
              <div className="mt-[2px] text-[13px] text-muted">
                {p.startDate === p.endDate ? dateShort(p.startDate) : `${dateShort(p.startDate)} → ${dateShort(p.endDate)}`}
                {p.productNames.length ? ` · ${p.productNames.join(", ")}` : ""}
              </div>
              {p.description && <div className="mt-[2px] line-clamp-2 whitespace-pre-line text-[13px] text-ink-2">{p.description}</div>}
            </div>
          );
        })}
      </IosGroup>
    </IosSection>
  );
}

/** Bandeau horizontal « Prochaines promos » (au-dessus du planning). */
export function PromoStrip({ promos, title = "Prochaines promos" }: { promos: PlanningPromoDTO[]; title?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-card-md bg-accent-soft px-4 py-3">
      <span className="mr-1 text-[12.5px] font-bold text-[oklch(0.3_0.03_152)]">{title}</span>
      {promos.length === 0 && <span className="text-[12.5px] text-muted">aucune dans les 30 prochains jours</span>}
      {promos.map((p) => {
        const [cls, label] = STATUS[p.status];
        return (
          <span
            key={p.id}
            title={[p.description, p.productNames.join(", ")].filter(Boolean).join("\n")}
            className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-[6px] text-[12px] ring-1 ring-line"
          >
            <span className="font-semibold">{p.name}</span>
            {p.discountPct != null && <span className="font-bold text-accent">−{p.discountPct} %</span>}
            <span className="text-muted-2">
              {p.startDate === p.endDate ? dateShort(p.startDate) : `${dateShort(p.startDate)} → ${dateShort(p.endDate)}`}
            </span>
            <Pill className={cls}>{label}</Pill>
          </span>
        );
      })}
    </div>
  );
}
